import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Handshake, Check, X, Loader2, Building2, MailOpen } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

const InviteCard = ({ inv, el, onAct }) => (
    <div className="fleet-card p-4 flex flex-wrap items-center gap-4 border-l-4 border-l-amber-500" data-testid={`invite-card-${inv.id}`}>
        <MailOpen className="w-6 h-6 text-amber-600 shrink-0" />
        <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800">{inv.leasing_company_name}</p>
            <p className="text-sm text-slate-500">{el ? `Σας προσκαλεί ως «${inv.workshop_name}» για ραντεβού συντήρησης των οχημάτων της.` : `Invites you as “${inv.workshop_name}” for maintenance appointments.`}</p>
        </div>
        <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" onClick={() => onAct(inv, 'accept')} data-testid={`invite-accept-${inv.id}`}><Check className="w-4 h-4 mr-1" />{el ? 'Αποδοχή' : 'Accept'}</Button>
        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onAct(inv, 'decline')} data-testid={`invite-decline-${inv.id}`}><X className="w-4 h-4 mr-1" />{el ? 'Απόρριψη' : 'Decline'}</Button>
    </div>
);

export default function WorkshopPartnersPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const [data, setData] = useState(null);

    const load = useCallback(() => api.get('/workshop/partners').then((r) => setData(r.data)).catch(() => setData({ invites: [], partners: [] })), []);
    useEffect(() => { load(); }, [load]);

    const act = (inv, action) => api.post(`/workshop/invites/${inv.id}/${action}`)
        .then(() => { toast.success(action === 'accept' ? (el ? 'Η συνεργασία ενεργοποιήθηκε' : 'Partnership activated') : (el ? 'Η πρόσκληση απορρίφθηκε' : 'Invite declined')); load(); })
        .catch((e) => toast.error(e.response?.data?.detail || 'Error'));

    if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal-700" />;
    return (
        <div className="space-y-6" data-testid="workshop-partners-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">{el ? 'Συνεργάτες Leasing' : 'Leasing partners'}</h1>
                <p className="text-zinc-500">{el ? 'Εταιρείες leasing που σας στέλνουν ραντεβού συντήρησης' : 'Leasing companies that send you maintenance appointments'}</p>
            </div>
            {data.invites.length > 0 && (
                <div className="space-y-3" data-testid="invites-list">
                    <h2 className="text-base md:text-lg font-semibold text-slate-700">{el ? 'Προσκλήσεις σε αναμονή' : 'Pending invites'}</h2>
                    {data.invites.map((inv) => <InviteCard key={inv.id} inv={inv} el={el} onAct={act} />)}
                </div>
            )}
            <div className="space-y-3" data-testid="partners-list">
                <h2 className="text-base md:text-lg font-semibold text-slate-700">{el ? 'Ενεργές συνεργασίες' : 'Active partnerships'}</h2>
                {data.partners.length ? data.partners.map((p) => (
                    <div key={p.id} className="fleet-card p-4 flex items-center gap-4" data-testid={`partner-card-${p.id}`}>
                        <Building2 className="w-6 h-6 text-[#1B7EB5]" />
                        <div className="flex-1">
                            <p className="font-semibold text-slate-800">{p.leasing_company_name}</p>
                            <p className="text-xs text-slate-500">{el ? 'Καταχώρηση ως' : 'Listed as'}: {p.name}</p>
                        </div>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-sm">{el ? 'Συνδεδεμένο' : 'Linked'}</span>
                    </div>
                )) : (
                    <div className="fleet-card p-8 text-center text-slate-400" data-testid="partners-empty">
                        <Handshake className="w-6 h-6 mx-auto mb-2" />
                        {el ? 'Δεν έχετε συνδεθεί ακόμα με εταιρεία leasing. Ζητήστε από την εταιρεία να σας στείλει πρόσκληση στο email του λογαριασμού σας.' : 'No leasing partners yet. Ask the leasing company to invite your account email.'}
                    </div>
                )}
            </div>
        </div>
    );
}
